import { ENV } from "../../config/runtimeEnv";
import { http } from "../../lib/http/client";
import type { UserUuid } from "../../types/identity";
import {
	PRESCRIPTION_PARTICULAR_KEYS,
	type CreatePrescriptionDraftRequest,
	type PrescriptionDraft,
	type PrescriptionParticulars,
	type UpdatePrescriptionDraftRequest,
} from "../../types/healthcare/PrescriptionDraftTypes";

const BASE = ENV.VITE_HEALTHCARE_SERVER;

export class PrescriptionDraftEnvelopeError extends Error {
	constructor() {
		super("Unexpected prescription draft response.");
		this.name = "PrescriptionDraftEnvelopeError";
	}
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const particularsOf = (value: unknown): PrescriptionParticulars => {
	if (!isRecord(value)) throw new PrescriptionDraftEnvelopeError();
	return Object.fromEntries(PRESCRIPTION_PARTICULAR_KEYS.map((key) => [key, value[key] ?? null])) as unknown as PrescriptionParticulars;
};

export const parsePrescriptionDraft = (value: unknown): PrescriptionDraft => {
	if (
		!isRecord(value) ||
		typeof value.uuid !== "string" ||
		typeof value.patient_uuid !== "string" ||
		typeof value.status !== "string" ||
		(value.modality !== "electronic" && value.modality !== "paper") ||
		typeof value.organisation_uuid !== "string" ||
		typeof value.prescriber_user_uuid !== "string" ||
		typeof value.revision !== "number"
	) {
		throw new PrescriptionDraftEnvelopeError();
	}
	return {
		uuid: value.uuid,
		patient_uuid: value.patient_uuid,
		status: value.status,
		modality: value.modality,
		organisation_uuid: value.organisation_uuid,
		prescriber_user_uuid: value.prescriber_user_uuid,
		prescriber_provider_uuid: typeof value.prescriber_provider_uuid === "string" ? value.prescriber_provider_uuid : "",
		particulars: particularsOf(value.particulars),
		revision: value.revision,
		updated_by_user_uuid: typeof value.updated_by_user_uuid === "string" ? value.updated_by_user_uuid : "",
		created_at: typeof value.created_at === "string" ? value.created_at : null,
		updated_at: typeof value.updated_at === "string" ? value.updated_at : null,
	};
};

const dataOf = (body: unknown): unknown => {
	if (!isRecord(body) || !("data" in body)) throw new PrescriptionDraftEnvelopeError();
	return body.data;
};

export const listPatientPrescriptionDrafts = async (patientUuid: UserUuid): Promise<PrescriptionDraft[]> => {
	const res = await http.get(`${BASE}/patients/${encodeURIComponent(patientUuid)}/prescriptions`);
	const data = dataOf(res.data);
	if (!Array.isArray(data)) throw new PrescriptionDraftEnvelopeError();
	return data.map(parsePrescriptionDraft);
};

export const fetchPrescriptionDraft = async (uuid: string): Promise<PrescriptionDraft> => {
	const res = await http.get(`${BASE}/prescriptions/${encodeURIComponent(uuid)}`);
	return parsePrescriptionDraft(dataOf(res.data));
};

// CU-14ygtkjeh2z: 201 is a new draft; 200 is Healthcare replaying the draft an earlier attempt with the same client_request_id already saved.
export const createPrescriptionDraft = async (
	patientUuid: UserUuid,
	payload: CreatePrescriptionDraftRequest
): Promise<{ draft: PrescriptionDraft; created: boolean }> => {
	const res = await http.post(`${BASE}/patients/${encodeURIComponent(patientUuid)}/prescriptions`, payload);
	return { draft: parsePrescriptionDraft(dataOf(res.data)), created: res.status === 201 };
};

export const updatePrescriptionDraft = async (uuid: string, payload: UpdatePrescriptionDraftRequest): Promise<PrescriptionDraft> => {
	const res = await http.patch(`${BASE}/prescriptions/${encodeURIComponent(uuid)}`, payload);
	return parsePrescriptionDraft(dataOf(res.data));
};
